import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  CheckCircle as ProcessedIcon,
  Schedule as PendingIcon,
  DoNotDisturb as SkippedIcon,
  Close as CloseIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { gebizAPI } from '../../services/api';

/**
 * PDF Selection Dialog
 * 
 * Allows users to select specific PDFs to extract from a job.
 * Shows processing status for each file.
 */
const PDFSelectionDialog = ({ 
  open, 
  onClose, 
  jobId,
  onExtract,
  extractLoading = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Fetch PDF list
  const { data: pdfData, isLoading, error, refetch } = useQuery(
    ['jobPDFs', jobId],
    () => gebizAPI.getPDFs(jobId),
    {
      enabled: open && !!jobId,
      onSuccess: () => {
        // Reset selection when dialog opens with fresh data
        setSelectedFiles([]);
      }
    }
  );

  // Separate files by status: pending, skipped, processed
  const { pendingFiles, skippedFiles, processedFiles } = useMemo(() => {
    if (!pdfData?.files) return { pendingFiles: [], skippedFiles: [], processedFiles: [] };
    
    return {
      pendingFiles: pdfData.files.filter(f => f.status === 'pending'),
      skippedFiles: pdfData.files.filter(f => f.status === 'skipped'),
      processedFiles: pdfData.files.filter(f => f.status === 'processed'),
    };
  }, [pdfData]);

  // Toggle file selection
  const toggleFile = (filename) => {
    setSelectedFiles(prev => 
      prev.includes(filename)
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  // Select all pending (not skipped, not processed)
  const selectAllPending = () => {
    setSelectedFiles(pendingFiles.map(f => f.filename));
  };

  // Select all files
  const selectAll = () => {
    setSelectedFiles(pdfData?.files.map(f => f.filename) || []);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedFiles([]);
  };

  // Handle extract
  const handleExtract = () => {
    if (selectedFiles.length > 0) {
      onExtract(selectedFiles);
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PdfIcon color="error" />
            <Typography variant="h6">Select PDFs to Extract</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load PDFs: {error.message}
            <Button size="small" onClick={() => refetch()} sx={{ ml: 2 }}>
              Retry
            </Button>
          </Alert>
        )}

        {/* Summary */}
        {pdfData && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip 
                label={`${pdfData.total_files} Total`} 
                size="small"
                variant="outlined"
              />
              <Chip 
                icon={<ProcessedIcon sx={{ fontSize: 16 }} />}
                label={`${pdfData.processed_files} Processed`} 
                size="small"
                color="success"
                variant="outlined"
              />
              {pdfData.skipped_files > 0 && (
                <Chip 
                  icon={<SkippedIcon sx={{ fontSize: 16 }} />}
                  label={`${pdfData.skipped_files} Skipped`} 
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
              <Chip 
                icon={<PendingIcon sx={{ fontSize: 16 }} />}
                label={`${pdfData.pending_files} Pending`} 
                size="small"
                color="warning"
                variant="outlined"
              />
            </Box>

            {/* Quick Actions */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button 
                size="small" 
                variant="outlined"
                startIcon={<SelectAllIcon />}
                onClick={selectAllPending}
                disabled={pendingFiles.length === 0}
              >
                Select Unprocessed ({pendingFiles.length})
              </Button>
              <Button 
                size="small" 
                variant="outlined"
                onClick={selectAll}
              >
                Select All
              </Button>
              <Button 
                size="small" 
                variant="outlined"
                onClick={clearSelection}
                disabled={selectedFiles.length === 0}
              >
                Clear
              </Button>
            </Box>

            {/* File List */}
            {pdfData.files.length === 0 ? (
              <Alert severity="info">
                No PDF files found for this job. Upload PDFs to R2 storage first.
              </Alert>
            ) : (
              <List dense sx={{ maxHeight: 350, overflow: 'auto' }}>
                {/* Pending Files Section */}
                {pendingFiles.length > 0 && (
                  <>
                    <ListItem sx={{ bgcolor: 'warning.light', py: 0.5 }}>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" color="warning.dark">
                            Pending Extraction ({pendingFiles.length})
                          </Typography>
                        }
                      />
                    </ListItem>
                    {pendingFiles.map((file) => (
                      <ListItem 
                        key={file.filename}
                        button
                        onClick={() => toggleFile(file.filename)}
                        sx={{ 
                          bgcolor: selectedFiles.includes(file.filename) ? 'action.selected' : 'inherit',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedFiles.includes(file.filename)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PdfIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={file.filename}
                          secondary={formatSize(file.size_bytes)}
                        />
                        <ListItemSecondaryAction>
                          <Chip 
                            icon={<PendingIcon sx={{ fontSize: 14 }} />}
                            label="Pending" 
                            size="small"
                            color="warning"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </>
                )}

                {/* Skipped Files Section */}
                {skippedFiles.length > 0 && (
                  <>
                    {pendingFiles.length > 0 && <Divider sx={{ my: 1 }} />}
                    <ListItem sx={{ bgcolor: 'grey.200', py: 0.5 }}>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" color="text.secondary">
                            Skipped - No Valid Awards ({skippedFiles.length})
                          </Typography>
                        }
                      />
                    </ListItem>
                    {skippedFiles.map((file) => (
                      <ListItem 
                        key={file.filename}
                        button
                        onClick={() => toggleFile(file.filename)}
                        sx={{ 
                          bgcolor: selectedFiles.includes(file.filename) ? 'action.selected' : 'inherit',
                          opacity: 0.7,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedFiles.includes(file.filename)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PdfIcon color="disabled" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={file.filename}
                          secondary={`${formatSize(file.size_bytes)} • No valid awards found`}
                        />
                        <ListItemSecondaryAction>
                          <Chip 
                            icon={<SkippedIcon sx={{ fontSize: 14 }} />}
                            label="Skipped" 
                            size="small"
                            color="default"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </>
                )}

                {/* Processed Files Section */}
                {processedFiles.length > 0 && (
                  <>
                    {(pendingFiles.length > 0 || skippedFiles.length > 0) && <Divider sx={{ my: 1 }} />}
                    <ListItem sx={{ bgcolor: 'success.light', py: 0.5 }}>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle2" color="success.dark">
                            Already Processed ({processedFiles.length})
                          </Typography>
                        }
                      />
                    </ListItem>
                    {processedFiles.map((file) => (
                      <ListItem 
                        key={file.filename}
                        button
                        onClick={() => toggleFile(file.filename)}
                        sx={{ 
                          bgcolor: selectedFiles.includes(file.filename) ? 'action.selected' : 'inherit',
                          opacity: 0.8,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedFiles.includes(file.filename)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PdfIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={file.filename}
                          secondary={`${formatSize(file.size_bytes)} • ${file.awards_count} awards extracted`}
                        />
                        <ListItemSecondaryAction>
                          <Chip 
                            icon={<ProcessedIcon sx={{ fontSize: 14 }} />}
                            label={`${file.awards_count} awards`}
                            size="small"
                            color="success"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </>
                )}
              </List>
            )}

            {/* Re-extraction Warning for processed files */}
            {selectedFiles.some(f => processedFiles.find(pf => pf.filename === f)) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                You've selected files that were already processed. Re-extracting may create duplicate awards.
              </Alert>
            )}
            
            {/* Info for skipped files */}
            {selectedFiles.some(f => skippedFiles.find(sf => sf.filename === f)) && 
             !selectedFiles.some(f => processedFiles.find(pf => pf.filename === f)) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You've selected skipped files. These had no valid awards on previous extraction - results may be the same.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={extractLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExtract}
            disabled={selectedFiles.length === 0 || extractLoading}
            startIcon={extractLoading ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
          >
            {extractLoading ? 'Starting...' : `Extract ${selectedFiles.length} PDF${selectedFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PDFSelectionDialog;

